/*
  Currency and Number Formatting Utilities
  -----------------------------------------
  Shared formatting functions for consistent display across components.
*/

// Free email provider domains - require website verification for these
export const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de',
  'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'msn.com',
  'aol.com', 'icloud.com', 'me.com', 'mac.com',
  'protonmail.com', 'proton.me', 'tutanota.com', 'tutamail.com',
  'mail.com', 'email.com', 'gmx.com', 'gmx.net', 'gmx.de',
  'yandex.com', 'yandex.ru', 'mail.ru', 'inbox.com',
  'zoho.com', 'fastmail.com', 'hushmail.com',
  'qq.com', '163.com', '126.com', 'sina.com',
  'rediffmail.com', 'web.de', 'freenet.de', 't-online.de',
  'orange.fr', 'laposte.net', 'free.fr', 'sfr.fr',
  'libero.it', 'virgilio.it', 'alice.it',
  'uol.com.br', 'bol.com.br', 'terra.com.br', 'globo.com', 'ig.com.br'
];

/**
 * Check if email is from a free provider
 * @param {string} email 
 * @returns {boolean}
 */
export function isFreeEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const domain = email.toLowerCase().split('@')[1];
  return domain && FREE_EMAIL_DOMAINS.includes(domain);
}

/**
 * Format currency based on locale and currency code
 * @param {number} amount 
 * @param {string} locale - e.g., 'en-US', 'pt-BR'
 * @param {string} currency - e.g., 'USD', 'BRL'
 * @returns {string}
 */
export function formatCurrency(amount, locale = 'en-US', currency = 'USD') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Get currency symbol for display
 * @param {string} currency 
 * @param {string} locale 
 * @returns {string}
 */
export function getCurrencySymbol(currency, locale = 'en-US') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).formatToParts(0).find(part => part.type === 'currency').value;
}

/**
 * Format revenue for display with M suffix for millions
 * @param {number} amount 
 * @param {string} currency 
 * @param {string} locale 
 * @returns {string}
 */
export function formatRevenueDisplay(amount, currency = 'USD', locale = 'en-US') {
  const currencySymbol = getCurrencySymbol(currency, locale);
  
  if (amount == null || isNaN(amount)) {
    return formatCurrency(0, locale, currency);
  }
  
  if (amount < 1000000) {
    return formatCurrency(amount, locale, currency);
  }
  
  // For amounts >= 1M, use standard M notation with one decimal
  const millions = amount / 1000000;
  return `${currencySymbol}${millions.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
}

/**
 * Format number with locale-appropriate separators (no decimals)
 * @param {number} amount 
 * @param {string} locale 
 * @returns {string}
 */
export function formatNumberWithCommas(amount, locale = 'en-US') {
  if (amount == null || isNaN(amount)) return '';
  return new Intl.NumberFormat(locale, { 
    maximumFractionDigits: 0,
    useGrouping: true
  }).format(amount);
}

/**
 * Format integer with locale-appropriate separators
 * @param {number} value 
 * @param {string} locale 
 * @returns {string}
 */
export function formatIntegerWithCommas(value, locale = 'en-US') {
  if (value == null || isNaN(value)) return '';
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.floor(value));
}

/**
 * Parse string like "$1,234,567" or "1.2M" to number
 * @param {string} value 
 * @returns {number}
 */
export function parseRevenueInput(value) {
  if (!value) return 0;
  const v = value.toString().trim().toUpperCase();
  // Support M suffix
  const mMatch = v.match(/^\$?\s*([0-9]*\.?[0-9]+)\s*M$/i);
  if (mMatch) {
    const millions = parseFloat(mMatch[1]);
    if (!isNaN(millions)) return Math.round(millions * 1000000);
  }
  // Fallback: strip non-digits
  const digits = v.replace(/[^0-9]/g, '');
  if (!digits) return 0;
  return parseInt(digits, 10);
}

/**
 * Parse integer input, stripping non-digits
 * @param {string} value 
 * @returns {number}
 */
export function parseIntegerInput(value) {
  if (!value) return 0;
  const digits = value.toString().replace(/[^0-9]/g, '');
  if (!digits) return 0;
  return parseInt(digits, 10);
}

/**
 * Available currencies for selection
 * Includes all Americas currencies plus major world currencies
 */
export const AVAILABLE_CURRENCIES = [
  // North America
  { code: 'USD', name: 'US Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'MXN', name: 'Mexican Peso' },
  // Central America
  { code: 'GTQ', name: 'Guatemalan Quetzal' },
  { code: 'BZD', name: 'Belize Dollar' },
  { code: 'HNL', name: 'Honduran Lempira' },
  { code: 'NIO', name: 'Nicaraguan Córdoba' },
  { code: 'CRC', name: 'Costa Rican Colón' },
  // Caribbean
  { code: 'CUP', name: 'Cuban Peso' },
  { code: 'DOP', name: 'Dominican Peso' },
  { code: 'HTG', name: 'Haitian Gourde' },
  { code: 'JMD', name: 'Jamaican Dollar' },
  { code: 'TTD', name: 'Trinidad & Tobago Dollar' },
  { code: 'BBD', name: 'Barbadian Dollar' },
  { code: 'BSD', name: 'Bahamian Dollar' },
  { code: 'KYD', name: 'Cayman Islands Dollar' },
  { code: 'AWG', name: 'Aruban Florin' },
  { code: 'ANG', name: 'Netherlands Antillean Guilder' },
  // South America
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'PEN', name: 'Peruvian Sol' },
  { code: 'VES', name: 'Venezuelan Bolívar' },
  { code: 'BOB', name: 'Bolivian Boliviano' },
  { code: 'PYG', name: 'Paraguayan Guaraní' },
  { code: 'UYU', name: 'Uruguayan Peso' },
  { code: 'GYD', name: 'Guyanese Dollar' },
  { code: 'SRD', name: 'Surinamese Dollar' },
  { code: 'FKP', name: 'Falkland Islands Pound' },
  // Europe
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'CHF', name: 'Swiss Franc' },
  // Other major currencies
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'INR', name: 'Indian Rupee' }
];
